import { memo, useCallback, useMemo, useState } from 'react';
import {
  Check,
  Eye,
  Filter,
  GraduationCap,
  MessageSquare,
  Pencil,
  RefreshCw,
  Search,
  X,
} from 'lucide-react';
import {
  Button,
  Flex,
  Paper,
  Stack,
  Text,
  Title,
  TextInput,
  Select,
  Badge,
  Group,
  Collapse,
  Box,
  Grid,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
  ColumnDef,
  SortingState,
  ColumnFiltersState,
} from '@tanstack/react-table';
import { TableLoadingSkeleton } from './TableLoadingSkeleton';
import {
  CourseDetailModal,
  type CourseDetail,
} from './CourseDetailModal';
import { CourseFeedbackModal } from './CourseFeedbackModal';

type Course = CourseDetail;

interface AdvancedCoursesTableProps {
  userCourses: Course[];
  loadingCourses: boolean;
  onEditCourse: (course: Course) => void;
}

// Memoized cell components for performance
const formatSectionLabel = (section: number | null | undefined): string => {
  const n = Number(section);
  if (!Number.isFinite(n) || n < 0) return '—';
  // DB/chapter.order is 0-based; show 1-based in admin UI
  return `Section ${n + 1}`;
};

const CourseNameCell = memo(({ course }: { course: Course }) => (
  <Text fw={500} size='sm'>
    {course.courseName}
  </Text>
));

const ProgressCell = memo(({ course }: { course: Course }) => (
  <Group gap='xs'>
    <Badge variant='light' size='sm' color='blue'>
      {formatSectionLabel(course.current_section)}
    </Badge>
    <Text size='xs' c='dimmed'>
      {Math.floor(course.current_time / 60)}m {course.current_time % 60}s
    </Text>
  </Group>
));

const StatusCell = memo(({ course }: { course: Course }) => {
  const completed = !!course.completed_at;
  const passed = !!course.passed_at;
  const reviewed = !!course.has_review;

  return (
    <Group gap='xs'>
      <Badge
        variant='light'
        size='sm'
        color={completed ? 'green' : 'red'}
        leftSection={completed ? <Check size={16} /> : <X size={16} />}
      >
        Completed
      </Badge>
      <Badge
        variant='light'
        size='sm'
        color={passed ? 'green' : 'red'}
        leftSection={passed ? <Check size={16} /> : <X size={16} />}
      >
        Passed
      </Badge>
      <Badge
        variant='light'
        size='sm'
        color={reviewed ? 'green' : 'red'}
        leftSection={reviewed ? <Check size={16} /> : <X size={16} />}
      >
        Reviewed
      </Badge>
    </Group>
  );
});

const ActionsCell = memo(
  ({
    course,
    onView,
    onEdit,
    onShowFeedback,
  }: {
    course: Course;
    onView: (course: Course) => void;
    onEdit: (course: Course) => void;
    onShowFeedback: (course: Course) => void;
  }) => (
    <Group gap='xs' wrap='nowrap'>
      <Button
        size='sm'
        variant='light'
        onClick={() => onView(course)}
        leftSection={<Eye size={16} />}
      >
        View
      </Button>
      {course.has_review || course.review ? (
        <Button
          size='sm'
          variant='light'
          onClick={() => onShowFeedback(course)}
          leftSection={<MessageSquare size={16} />}
        >
          Mostra feedback
        </Button>
      ) : null}
      <Button
        size='sm'
        variant='light'
        onClick={() => onEdit(course)}
        leftSection={<Pencil size={16} />}
      >
        Edit
      </Button>
    </Group>
  )
);

// Memoized filter component
const CoursesFilter = memo(
  ({
    globalFilter,
    setGlobalFilter,
    statusFilter,
    setStatusFilter,
  }: {
    globalFilter: string;
    setGlobalFilter: (value: string) => void;
    statusFilter: string;
    setStatusFilter: (value: string) => void;
  }) => {
    const [filtersOpen, { toggle: toggleFilters }] = useDisclosure(false);

    return (
      <Box>
        <Flex justify='space-between' align='center' mb='md'>
          <TextInput
            placeholder='Search courses...'
            value={globalFilter}
            onChange={e => setGlobalFilter(e.target.value)}
            leftSection={<Search size={16} />}
            size='sm'
            w={300}
          />
          <Button
            variant='light'
            size='sm'
            onClick={toggleFilters}
            leftSection={<Filter size={16} />}
          >
            Filters
          </Button>
        </Flex>

        <Collapse in={filtersOpen}>
          <Grid mb='md'>
            <Grid.Col span={6}>
              <Select
                label='Status'
                placeholder='All statuses'
                value={statusFilter}
                onChange={value => setStatusFilter(value || '')}
                data={[
                  { value: '', label: 'All statuses' },
                  { value: 'completed', label: 'Completed' },
                  { value: 'passed', label: 'Passed' },
                  { value: 'reviewed', label: 'Reviewed' },
                  { value: 'in-progress', label: 'In Progress' },
                ]}
                size='sm'
              />
            </Grid.Col>
          </Grid>
        </Collapse>
      </Box>
    );
  }
);

CoursesFilter.displayName = 'CoursesFilter';

export const AdvancedCoursesTable = memo<AdvancedCoursesTableProps>(
  ({ userCourses, loadingCourses, onEditCourse }) => {
    const [sorting, setSorting] = useState<SortingState>([]);
    const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
    const [globalFilter, setGlobalFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [viewedCourse, setViewedCourse] = useState<Course | undefined>();
    const [feedbackCourse, setFeedbackCourse] = useState<Course | undefined>();

    const handleViewCourse = useCallback((course: Course) => {
      setViewedCourse(course);
    }, []);

    const handleCloseDetail = useCallback(() => {
      setViewedCourse(undefined);
    }, []);

    const handleShowFeedback = useCallback((course: Course) => {
      setFeedbackCourse(course);
    }, []);

    const handleCloseFeedback = useCallback(() => {
      setFeedbackCourse(undefined);
    }, []);

    // Enhanced filtering logic
    const filteredCourses = useMemo(() => {
      let filtered = userCourses;

      // Global search
      if (globalFilter) {
        const search = globalFilter.toLowerCase();
        filtered = filtered.filter(course =>
          course.courseName.toLowerCase().includes(search)
        );
      }

      // Status filter
      if (statusFilter) {
        filtered = filtered.filter(course => {
          if (statusFilter === 'completed') return !!course.completed_at;
          if (statusFilter === 'passed') return !!course.passed_at;
          if (statusFilter === 'reviewed') return !!course.has_review;
          if (statusFilter === 'in-progress') return !course.completed_at;
          return true;
        });
      }

      return filtered;
    }, [userCourses, globalFilter, statusFilter]);

    // Column definitions with performance optimizations
    const columns = useMemo<ColumnDef<Course>[]>(
      () => [
        {
          accessorKey: 'courseName',
          header: 'Course Name',
          cell: ({ row }) => <CourseNameCell course={row.original} />,
          enableSorting: true,
          enableGlobalFilter: true,
        },
        {
          accessorKey: 'current_section',
          header: 'Progress',
          cell: ({ row }) => <ProgressCell course={row.original} />,
          enableSorting: true,
          sortingFn: (rowA, rowB) => {
            return (
              rowA.original.current_section - rowB.original.current_section
            );
          },
        },
        {
          accessorKey: 'status',
          header: 'Status',
          cell: ({ row }) => <StatusCell course={row.original} />,
          enableSorting: false,
          enableGlobalFilter: false,
        },
        {
          id: 'actions',
          header: 'Actions',
          cell: ({ row }) => (
            <ActionsCell
              course={row.original}
              onView={handleViewCourse}
              onEdit={onEditCourse}
              onShowFeedback={handleShowFeedback}
            />
          ),
          enableSorting: false,
          enableGlobalFilter: false,
        },
      ],
      [handleViewCourse, handleShowFeedback, onEditCourse]
    );

    const table = useReactTable({
      data: filteredCourses,
      columns,
      state: {
        sorting,
        columnFilters,
        globalFilter,
      },
      onSortingChange: setSorting,
      onColumnFiltersChange: setColumnFilters,
      onGlobalFilterChange: setGlobalFilter,
      getCoreRowModel: getCoreRowModel(),
      getFilteredRowModel: getFilteredRowModel(),
      getSortedRowModel: getSortedRowModel(),
    });

    const clearFilters = useCallback(() => {
      setGlobalFilter('');
      setStatusFilter('');
    }, []);

    return (
      <Paper withBorder shadow='md' p='md'>
        <Flex justify='space-between' align='center' mb='md'>
          <Title order={4}>Courses Management</Title>
          <Group gap='xs'>
            <Button
              variant='light'
              size='sm'
              onClick={() => window.location.reload()}
              loading={loadingCourses}
              leftSection={<RefreshCw size={16} />}
            >
              Refresh
            </Button>
          </Group>
        </Flex>

        <CoursesFilter
          globalFilter={globalFilter}
          setGlobalFilter={setGlobalFilter}
          statusFilter={statusFilter}
          setStatusFilter={setStatusFilter}
        />

        {loadingCourses ? (
          <TableLoadingSkeleton rows={5} columns={4} />
        ) : filteredCourses.length === 0 ? (
          <Stack align='center' gap='md' py='xl'>
            <Text c='dimmed' size='lg'>
              {userCourses.length === 0
                ? 'No courses found'
                : 'No courses match your filters'}
            </Text>
            {userCourses.length > 0 && (
              <Button variant='light' onClick={clearFilters}>
                Clear Filters
              </Button>
            )}
          </Stack>
        ) : (
          <Box>
            <Box
              style={{
                border: '1px solid #e9ecef',
                borderRadius: '8px',
                overflow: 'hidden',
              }}
            >
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead style={{ backgroundColor: '#f8f9fa' }}>
                  {table.getHeaderGroups().map(headerGroup => (
                    <tr key={headerGroup.id}>
                      {headerGroup.headers.map(header => (
                        <th
                          key={header.id}
                          style={{
                            padding: '12px 16px',
                            textAlign: 'left',
                            fontWeight: 600,
                            fontSize: '14px',
                            borderBottom: '1px solid #e9ecef',
                            cursor: header.column.getCanSort()
                              ? 'pointer'
                              : 'default',
                          }}
                          onClick={header.column.getToggleSortingHandler()}
                        >
                          <Flex align='center' gap='xs'>
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                            {header.column.getIsSorted() === 'asc' && ' ↑'}
                            {header.column.getIsSorted() === 'desc' && ' ↓'}
                          </Flex>
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody>
                  {table.getRowModel().rows.map(row => (
                    <tr
                      key={row.id}
                      style={{
                        borderBottom: '1px solid #f1f3f4',
                        transition: 'background-color 0.2s',
                      }}
                      onMouseEnter={e => {
                        e.currentTarget.style.backgroundColor = '#f8f9fa';
                      }}
                      onMouseLeave={e => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }}
                    >
                      {row.getVisibleCells().map(cell => (
                        <td
                          key={cell.id}
                          style={{
                            padding: '12px 16px',
                            fontSize: '14px',
                          }}
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </Box>

            {filteredCourses.length > 0 && (
              <Flex justify='space-between' align='center' mt='md'>
                <Text size='sm' c='dimmed'>
                  Showing {filteredCourses.length} of {userCourses.length}{' '}
                  courses
                </Text>
                {globalFilter || statusFilter ? (
                  <Button variant='light' size='xs' onClick={clearFilters}>
                    Clear Filters
                  </Button>
                ) : null}
              </Flex>
            )}
          </Box>
        )}

        {userCourses.length > 0 && (
          <Box
            mt='md'
            p='md'
            style={{
              backgroundColor: '#f8f9fa',
              borderRadius: '8px',
              border: '1px solid #e9ecef',
            }}
          >
            <Text size='sm' c='dimmed'>
              <GraduationCap
                size={16}
                style={{ marginRight: '8px', display: 'inline' }}
              />
              Use View to inspect quiz answers. Use Mostra feedback for course
              review ratings. Use Edit to modify course progress and completion
              status.
            </Text>
          </Box>
        )}

        <CourseDetailModal
          opened={viewedCourse !== undefined}
          onClose={handleCloseDetail}
          course={viewedCourse}
        />
        <CourseFeedbackModal
          opened={feedbackCourse !== undefined}
          onClose={handleCloseFeedback}
          course={feedbackCourse}
        />
      </Paper>
    );
  }
);

AdvancedCoursesTable.displayName = 'AdvancedCoursesTable';
